import { TranslateService } from '@ngx-translate/core';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { CompositeModel } from '../complex/composite.model';
import { BooleanModel } from '../primitive/boolean.model';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';

const odataConstructionIncompleteSet = 'ConstructionIncompleteSet';
const odataNetworkNotOperationalSet = 'NetworkNotOperationalSet';
const odataServiceIssuesSet = 'ServiceIssuesSet';
const odataOpenIssuesSet = 'OpenIssuesSet';
const odataEquipmentNotOperationalSet = 'EquipmentNotOperationalSet';
const odataPhysicistInspectionNotCompleteSet = 'PhysicistInspectionNotCompleteSet';
const odataOemIssuesSet = 'OemIssuesSet';

export class ConfirmationInterruptionsModel extends CompositeModel {
    public constructionIncompleteText: MultilineTextModel;
    public networkNotFullyOperationalText: MultilineTextModel;
    public serviceIssuesText: MultilineTextModel;
    public openIssuesText: MultilineTextModel;
    public equipmentNotFullyOperationalText: MultilineTextModel;
    public statePhysicistInspectionNotCompleteText: MultilineTextModel;
    public oemIssuesText: MultilineTextModel;

    public constructionIncomplete: DisplayDecorator<BooleanModel>;
    public networkNotFullyOperational: DisplayDecorator<BooleanModel>;
    public serviceIssues: DisplayDecorator<BooleanModel>;
    public openIssues: DisplayDecorator<BooleanModel>;
    public equipmentNotFullyOperational: DisplayDecorator<BooleanModel>;
    public statePhysicistInspectionNotComplete: DisplayDecorator<BooleanModel>;
    public oemIssues: DisplayDecorator<BooleanModel>;
    public constructor() {
        super('ConfirmationInterruptionsModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);

        this.constructionIncomplete = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_CONSTRUCTION_INCOMPLETE'),
            new BooleanModel('ConstructionIncompleteSelected'),
        );
        this.networkNotFullyOperational = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_NETWORK_NOT_FULLY_OPERATIONAL'),
            new BooleanModel('NetworkNotFullyOperationalSelected'),
        );
        this.serviceIssues = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_SERVICE_ISSUES'),
            new BooleanModel('ServiceIssuesSelected'),
        );
        this.openIssues = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_OPEN_ISSUES'),
            new BooleanModel('OpenIssuesSelected'),
        );
        this.equipmentNotFullyOperational = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_EQUIPMENT_NOT_FULLY_OPERATIONAL'),
            new BooleanModel('EquipmentNotFullyOperationalSelected'),
        );
        this.statePhysicistInspectionNotComplete = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_STATE_PHYSICIST_INSPECTION_NOT_COMPLETE'),
            new BooleanModel('StatePhysicistInspectionNotCompleteSelected'),
        );
        this.oemIssues = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('CONFIRMATION_INTERRUPTIONS_OEM_ISSUES'),
            new BooleanModel('OemIssuesSelected'),
        );

        this.constructionIncompleteText = <MultilineTextModel>(
            this.set(new MultilineTextModel(odataConstructionIncompleteSet))
        );
        this.networkNotFullyOperationalText = <MultilineTextModel>(
            this.set(new MultilineTextModel(odataNetworkNotOperationalSet))
        );
        this.serviceIssuesText = <MultilineTextModel>this.set(new MultilineTextModel(odataServiceIssuesSet));
        this.openIssuesText = <MultilineTextModel>this.set(new MultilineTextModel(odataOpenIssuesSet));
        this.equipmentNotFullyOperationalText = <MultilineTextModel>(
            this.set(new MultilineTextModel(odataEquipmentNotOperationalSet))
        );
        this.statePhysicistInspectionNotCompleteText = <MultilineTextModel>(
            this.set(new MultilineTextModel(odataPhysicistInspectionNotCompleteSet))
        );
        this.oemIssuesText = <MultilineTextModel>this.set(new MultilineTextModel(odataOemIssuesSet));
    }

    public getOutModel(): ODataConfirmationInterruptionsModelOutModel {
        const outModel = <ODataConfirmationInterruptionsModelOutModel>super.getOutModel();

        // 1. The user ticks the checkbox and the textbox appears
        // 2. The user is typing something in the textbox.
        // 3. The user un-ticking the checkbox - the value should still be there, but the textbox is hidden
        // 4. If the user ticks again the checkbox - the previous value should now be displayed.
        // 5. Only when the User is leaving the Page or clicking on Save, the value of the textbox will be emptied if the checkbox is not checked.

        outModel[odataConstructionIncompleteSet] = this.constructionIncomplete.value
            ? outModel[odataConstructionIncompleteSet]
            : [];
        outModel[odataNetworkNotOperationalSet] = this.networkNotFullyOperational.value
            ? outModel[odataNetworkNotOperationalSet]
            : [];
        outModel[odataServiceIssuesSet] = this.serviceIssues.value ? outModel[odataServiceIssuesSet] : [];
        outModel[odataOpenIssuesSet] = this.openIssues.value ? outModel[odataOpenIssuesSet] : [];
        outModel[odataEquipmentNotOperationalSet] = this.equipmentNotFullyOperational.value
            ? outModel[odataEquipmentNotOperationalSet]
            : [];
        outModel[odataPhysicistInspectionNotCompleteSet] = this.statePhysicistInspectionNotComplete.value
            ? outModel[odataPhysicistInspectionNotCompleteSet]
            : [];
        outModel[odataOemIssuesSet] = this.oemIssues.value ? outModel[odataOemIssuesSet] : [];
        return outModel;
    }

    public toJSON(): unknown {
        // only save the values if the user selected the checkbox;
        return {
            constructionIncompleteText: this.constructionIncomplete.value ? this.constructionIncompleteText.value : '',
            networkNotFullyOperationalText: this.networkNotFullyOperational.value
                ? this.networkNotFullyOperationalText.value
                : '',
            serviceIssuesText: this.serviceIssues.value ? this.serviceIssuesText.value : '',
            openIssuesText: this.openIssues.value ? this.openIssuesText.value : '',
            equipmentNotFullyOperationalText: this.equipmentNotFullyOperational.value
                ? this.equipmentNotFullyOperationalText.value
                : '',
            statePhysicistInspectionNotCompleteText: this.statePhysicistInspectionNotComplete.value
                ? this.statePhysicistInspectionNotCompleteText.value
                : '',
            oemIssuesText: this.oemIssues.value ? this.oemIssuesText.value : '',
        };
    }
}

export class ConfirmationInterruptionsModelFromJson extends ConfirmationInterruptionsModel {
    public constructor(data: JsonConfirmationInterruptionsModel) {
        super();
        this.constructionIncompleteText.value = data.constructionIncompleteText;
        this.constructionIncomplete.value = !!data.constructionIncompleteText;
        this.networkNotFullyOperationalText.value = data.networkNotFullyOperationalText;
        this.networkNotFullyOperational.value = !!data.networkNotFullyOperationalText;
        this.serviceIssuesText.value = data.serviceIssuesText;
        this.serviceIssues.value = !!data.serviceIssuesText;
        this.openIssuesText.value = data.openIssuesText;
        this.openIssues.value = !!data.openIssuesText;
        this.equipmentNotFullyOperationalText.value = data.equipmentNotFullyOperationalText;
        this.equipmentNotFullyOperational.value = !!data.equipmentNotFullyOperationalText;
        this.statePhysicistInspectionNotCompleteText.value = data.statePhysicistInspectionNotCompleteText;
        this.statePhysicistInspectionNotComplete.value = !!data.statePhysicistInspectionNotCompleteText;
        this.oemIssuesText.value = data.oemIssuesText;
        this.oemIssues.value = !!data.oemIssuesText;
    }
}

export interface JsonConfirmationInterruptionsModel {
    constructionIncompleteText: string;
    networkNotFullyOperationalText: string;
    serviceIssuesText: string;
    openIssuesText: string;
    equipmentNotFullyOperationalText: string;
    statePhysicistInspectionNotCompleteText: string;
    oemIssuesText: string;
}

export interface ODataConfirmationInterruptionsModelOutModel extends ODataObject {
    ConstructionIncompleteSet: ODataTextLineOutModel[];
    NetworkNotOperationalSet: ODataTextLineOutModel[];
    ServiceIssuesSet: ODataTextLineOutModel[];
    OpenIssuesSet: ODataTextLineOutModel[];
    EquipmentNotOperationalSet: ODataTextLineOutModel[];
    PhysicistInspectionNotCompleteSet: ODataTextLineOutModel[];
    OemIssuesSet: ODataTextLineOutModel[];
}
